import { Container, Graphics } from 'pixi.js';
import type { Input, MenuAction } from '../input/Input';
import { COLORS } from './theme';

export interface FocusItem {
  readonly container: Container;
  activate(): void;
}

const OUTLINE_PADDING = 6;
const OUTLINE_RADIUS = 12;
const OUTLINE_WIDTH = 3;

// Keyboard/gamepad focus for menu scenes: draws a highlight outline around
// the active item and moves focus in response to Input's menu navigation
// events (up/down/left/right/confirm). Nothing ever repositions - no hover
// "lift" - only the outline's own geometry/visibility changes. Mouse
// interaction on the widgets themselves (pointertap) is untouched and keeps
// working alongside this. 'back' is intentionally not handled here; scenes
// listen for it directly on input.menu (it usually means "leave the scene",
// which is scene-specific, not a focus concept).
export class FocusRing {
  private items: readonly FocusItem[] = [];
  private index = 0;
  private readonly outline = new Graphics();
  private readonly onMenu = (e: Event): void => {
    this.handleMenu((e as CustomEvent<MenuAction>).detail);
  };

  constructor(
    layer: Container,
    private readonly input: Input,
  ) {
    this.outline.visible = false;
    layer.addChild(this.outline);
    this.input.menu.addEventListener('menu', this.onMenu);
  }

  get focusedIndex(): number {
    return this.index;
  }

  setItems(items: readonly FocusItem[], focusIndex = 0): void {
    this.items = items;
    this.index = items.length === 0 ? 0 : clampIndex(focusIndex, items.length);
    this.render();
  }

  dispose(): void {
    this.input.menu.removeEventListener('menu', this.onMenu);
    this.outline.destroy();
  }

  private handleMenu(action: MenuAction): void {
    if (this.items.length === 0) return;
    switch (action) {
      case 'up':
      case 'left':
        this.move(-1);
        break;
      case 'down':
      case 'right':
        this.move(1);
        break;
      case 'confirm':
        this.items[this.index]?.activate();
        break;
      case 'back':
        break;
    }
  }

  private move(delta: number): void {
    const n = this.items.length;
    this.index = ((this.index + delta) % n + n) % n;
    this.render();
  }

  // Draws the outline around the focused item's bounds, converted into the
  // ring's own parent space. `item.container` can sit arbitrarily deep under
  // `layer` (e.g. a seat card's swatch, nested card > swatch), so combining
  // getLocalBounds() (bounds in the item's OWN local space) with only its
  // immediate x/y ignored every ancestor transform in between and drew the
  // outline near the origin - the stray top-left rectangle bug. getBounds()
  // gives world/screen-space bounds regardless of nesting depth, and
  // toLocal() maps that into the outline's actual parent, so this is correct
  // no matter how deep the focused item lives in the tree.
  private render(): void {
    const item = this.items[this.index];
    this.outline.clear();
    const parent = this.outline.parent;
    if (!item || !parent) {
      this.outline.visible = false;
      return;
    }
    const bounds = item.container.getBounds();
    const topLeft = parent.toLocal({ x: bounds.x, y: bounds.y });
    const bottomRight = parent.toLocal({ x: bounds.x + bounds.width, y: bounds.y + bounds.height });
    this.outline
      .roundRect(
        topLeft.x - OUTLINE_PADDING,
        topLeft.y - OUTLINE_PADDING,
        bottomRight.x - topLeft.x + OUTLINE_PADDING * 2,
        bottomRight.y - topLeft.y + OUTLINE_PADDING * 2,
        OUTLINE_RADIUS,
      )
      .stroke({ width: OUTLINE_WIDTH, color: COLORS.accentYellow });
    this.outline.visible = true;
  }
}

function clampIndex(i: number, length: number): number {
  return Math.min(Math.max(i, 0), length - 1);
}
